import styled from "@emotion/styled";
import { formatDistanceToNow } from "date-fns";
import { enNZ } from "date-fns/locale/en-NZ";
import { forwardRef } from "react";

import { Cell, ConfirmationDialog, Icon, SkiliticsIcon, TableRow, Tooltip } from "@skilitics/designer-ui-components";
import type { InteractModuleListItem } from "@skilitics/nautilus-endpoint-definition";

import { UserAvatar } from "../../../../ui/UserAvatar";
import { DuplicateModuleWizard } from "../../../wizards";
import { ModuleMenu } from "../ModuleMenu";
import { useModuleItem } from "../shared";
import { columns, type Column } from "./columns";
import {
  Description,
  DetailsContainer,
  IconContainer,
  IconWrapper,
  Label,
  MenuContainer,
  Tags,
  Thumbnail,
  Title,
} from "./ListItemElements";

type Props = {
  module: InteractModuleListItem;
  title: string;
};

const getColumnAlignment = (id: Column["id"]) => columns.find((column) => column.id === id)?.align;

const StyledAvatar = styled(UserAvatar)`
  width: 30px;
  height: 30px;
  border: 2px solid ${({ theme }) => theme.primary};
`;

export const ListItem = forwardRef<HTMLTableRowElement, Props>(({ module, title: moduleTitle }, ref) => {
  const {
    formatMessage,
    moduleUrl,
    url,
    busy,
    hovered,
    setBusy,
    alreadyOpenMessage,
    handleMenuOpen,
    handleMenuClose,
    handleMenuSelection,
    deleteDialogProps,
    duplicateWizardProps,
  } = useModuleItem(module);

  return (
    <>
      <TableRow
        ref={ref}
        key={module.id}
        onClick={() => (window.location.href = moduleUrl)}
        data-test="list-module-card-container"
        data-hovered={hovered}
      >
        <Cell align={getColumnAlignment("thumbnail")}>
          <Thumbnail>
            <img src={url} alt={formatMessage("thumbnail")} />
            <IconContainer>
              {module.alreadyOpenUser && (
                <Tooltip title={alreadyOpenMessage} placement="top">
                  <StyledAvatar email={module.alreadyOpenUser.email} />
                </Tooltip>
              )}
              <IconWrapper $show={!!module.readonly} $busy={busy === "deleting"}>
                <Icon icon="trash-alt" size={15} />
              </IconWrapper>
              <IconWrapper $show={!!module.readonly} $busy={busy === "locking"}>
                <SkiliticsIcon icon="lock" size={15} />
              </IconWrapper>
            </IconContainer>
          </Thumbnail>
        </Cell>
        <Cell align={getColumnAlignment("titleAndDescription")}>
          <DetailsContainer>
            <Title>{moduleTitle}</Title>
            <Description>{module.learningObjective}</Description>
          </DetailsContainer>
        </Cell>
        <Cell align={getColumnAlignment("tags")}>
          <Tags>{module.tags?.join(", ") ?? ""}</Tags>
        </Cell>
        <Cell align={getColumnAlignment("modified")}>
          <Label>{formatDistanceToNow(module.updatedOn, { addSuffix: true, locale: enNZ })}</Label>
        </Cell>
        <Cell align={getColumnAlignment("lock")} onClick={(event) => event.stopPropagation()}>
          <MenuContainer data-test="module-actions-container">
            <ModuleMenu
              top="12px"
              module={module}
              busy={busy}
              setBusy={setBusy}
              onMenuOpen={handleMenuOpen}
              onMenuClose={handleMenuClose}
              onMenuSelection={handleMenuSelection}
            />
          </MenuContainer>
        </Cell>
      </TableRow>
      <ConfirmationDialog {...deleteDialogProps} />
      <DuplicateModuleWizard {...duplicateWizardProps} />
    </>
  );
});
ListItem.displayName = "ListItem";
